import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Package, Loader } from 'lucide-react';
import { useOrder } from '@/hooks/useApi';
import { formatPrice } from '@/utils';

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id') ?? '';
  const { data: order, isLoading } = useOrder(orderId);

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm border border-gray-200 dark:border-zinc-800 p-8 max-w-md w-full text-center">
        {isLoading || !order ? (
          <>
            <Loader className="h-12 w-12 text-brand-500 animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-zinc-100">Processing your order…</h1>
            <p className="text-gray-500 dark:text-zinc-500 mt-2 text-sm">Confirming payment with Stripe</p>
          </>
        ) : order.status === 'paid' || order.status === 'processing' || order.status === 'shipped' ? (
          <>
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-zinc-100 mb-2">Payment successful!</h1>
            <p className="text-gray-500 dark:text-zinc-500 mb-6">
              Your order for <span className="font-medium text-gray-800 dark:text-zinc-200">{order.card_name}</span> has been placed.
              The seller will ship it to you shortly.
            </p>

            <div className="bg-white dark:bg-zinc-900 rounded-xl p-4 text-left mb-6 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-zinc-500">Order ID</span>
                <span className="font-mono text-xs text-gray-600 dark:text-zinc-400">{order.id.slice(0, 8)}…</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-zinc-500">Total paid</span>
                <span className="font-semibold">{formatPrice(order.total)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-zinc-500">Status</span>
                <span className="capitalize font-medium text-green-700 dark:text-green-300">{order.status}</span>
              </div>
              {order.tracking_number && (
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-zinc-500">Tracking</span>
                  <span className="font-medium">{order.tracking_number}</span>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Link to="/dashboard" className="btn-primary px-6 py-2.5">Go to Dashboard</Link>
              <Link to="/marketplace" className="text-sm text-brand-600 dark:text-brand-400 hover:underline">Continue shopping</Link>
            </div>
          </>
        ) : order.status === 'pending' ? (
          <>
            <Loader className="h-12 w-12 text-brand-500 animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-zinc-100">Confirming payment…</h1>
            <p className="text-gray-500 dark:text-zinc-500 mt-2 text-sm">This usually takes a few seconds.</p>
          </>
        ) : (
          <>
            <Package className="h-12 w-12 text-gray-500 dark:text-zinc-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-zinc-100 mb-2">Order status: {order.status}</h1>
            <Link to="/dashboard" className="btn-primary px-6 py-2.5">Go to Dashboard</Link>
          </>
        )}
      </div>
    </div>
  );
}
